/**
 * LipSync & Viseme Mapping Engine for animated speech synchronization.
 */
export type VisemeType = 'rest' | 'A' | 'E' | 'I' | 'O' | 'U' | 'MBP' | 'FV' | 'LNTD' | 'wide' | 'smile';

export interface VisemeShape {
  d: string; // SVG path data
  fill?: string;
  stroke?: string;
}

export class LipSync {
  public static VISEMES: Record<VisemeType, VisemeShape> = {
    // Rest - closed neutral mouth
    rest: {
      d: 'M -14 0 Q 0 2 14 0 Q 0 -1 -14 0 Z',
      fill: '#be185d'
    },
    // A / Ah - open wide oval
    A: {
      d: 'M -14 -4 Q 0 -14 14 -4 Q 16 12 0 14 Q -16 12 -14 -4 Z',
      fill: '#881337'
    },
    // E / Eh - wide open smile
    E: {
      d: 'M -16 -2 Q 0 -6 16 -2 Q 12 8 0 8 Q -12 8 -16 -2 Z',
      fill: '#9d174d'
    },
    // I / Ee - stretched teeth showing
    I: {
      d: 'M -18 0 Q 0 -3 18 0 Q 14 4 0 5 Q -14 4 -18 0 Z',
      fill: '#f43f5e'
    },
    // O / Oh - round circle
    O: {
      d: 'M -8 -8 Q 0 -14 8 -8 Q 12 0 8 8 Q 0 14 -8 8 Q -12 0 -8 -8 Z',
      fill: '#4c0519'
    },
    // U / Oo - small tight circle
    U: {
      d: 'M -5 -5 Q 0 -9 5 -5 Q 8 0 5 5 Q 0 9 -5 5 Q -8 0 -5 -5 Z',
      fill: '#4c0519'
    },
    // M, B, P - pressed lips
    MBP: {
      d: 'M -16 1 Q 0 -1 16 1 Q 0 3 -16 1 Z',
      fill: '#9d174d'
    },
    // F, V - lower lip tucked
    FV: {
      d: 'M -15 0 Q 0 -4 15 0 Q 12 5 0 6 Q -12 5 -15 0 Z',
      fill: '#be185d'
    },
    // L, N, T, D - tongue touching roof
    LNTD: {
      d: 'M -14 -3 Q 0 -8 14 -3 Q 12 6 0 7 Q -12 6 -14 -3 Z',
      fill: '#881337'
    },
    // Wide laugh
    wide: {
      d: 'M -18 -4 Q 0 -10 18 -4 Q 14 14 0 16 Q -14 14 -18 -4 Z',
      fill: '#4c0519'
    },
    // Soft smile
    smile: {
      d: 'M -14 0 Q 0 8 14 0 Q 0 2 -14 0 Z',
      fill: '#be185d'
    }
  };

  /**
   * Map English letter/phoneme to Viseme.
   */
  public static charToViseme(char: string): VisemeType {
    const c = char.toUpperCase();
    if (['A'].includes(c)) return 'A';
    if (['E'].includes(c)) return 'E';
    if (['I', 'Y'].includes(c)) return 'I';
    if (['O'].includes(c)) return 'O';
    if (['U', 'W'].includes(c)) return 'U';
    if (['M', 'B', 'P'].includes(c)) return 'MBP';
    if (['F', 'V'].includes(c)) return 'FV';
    if (['L', 'N', 'T', 'D', 'S', 'Z', 'R'].includes(c)) return 'LNTD';
    return 'rest';
  }

  /**
   * Procedural Viseme sequence generator for spoken text strings.
   */
  public static textToVisemes(text: string, duration: number): { time: number; viseme: VisemeType }[] {
    const sequence: { time: number; viseme: VisemeType }[] = [];
    const clean = text.replace(/[^a-zA-Z]/g, '');
    if (clean.length === 0 || duration <= 0) return [{ time: 0, viseme: 'rest' }];

    const timePerChar = duration / clean.length;
    for (let i = 0; i < clean.length; i++) {
      sequence.push({
        time: i * timePerChar,
        viseme: LipSync.charToViseme(clean[i])
      });
    }
    sequence.push({ time: duration, viseme: 'rest' });
    return sequence;
  }
}
